import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Field, Form, FormElement, FormRenderProps } from "@progress/kendo-react-form";
import { Error, Label } from "@progress/kendo-react-labels";
import {
  Upload,
  UploadListItemProps,
  UploadOnAddEvent,
  UploadOnRemoveEvent,
} from "@progress/kendo-react-upload";
import { useEffect, useState } from "react";
import { EmailListEditor } from ".";
import companyApi from "../../api/company";
import productsApi from "../../api/products";
import { useAgreementDocuments } from "../../hooks/useAgreementDocuments";
import { useApi, useFetch } from "../../hooks/useApi";
import { useUserContext } from "../../hooks/useUserContext";
import { objectToLookup } from "../../utilities/objectUtilities";
import { alphaNumericValidator, requiredValidator } from "../../validators";
import { FormDropDownList, FormInput, FormSwitch, FormSwitchList, ListItem } from "../form";
import { LoadingIndicator } from "../LoadingIndicator";
import { Commodity, ICommodity } from "../quotes";
import { HeaderTextSelector, PdfFileItem, ViewPdfDocumentDialog } from "../utilities";
import { Company } from "./Company";

interface AddEditCompanyFormProps {
  cancelEdit: () => void;
  onSubmit: () => void;
  company: any;
  companies: Company[];
  roles: ListItem[];
  businessTypes: ListItem[];
  features: ListItem[];
  roleFeatures: Record<string, Record<number, boolean>>;
  parentChildRoles: Record<string, string>;
}

const GENERAL_VIEW = "Company Info";
const DOCUMENTS_VIEW = "Documents";
const CONTACTS_VIEW = "Contacts";

export const AddEditCompanyDialog = (props: AddEditCompanyFormProps) => {
  const { company, companies, roles, businessTypes, features, roleFeatures, parentChildRoles } =
    props;
  const { context: user } = useUserContext();
  const companyId = company.id;
  const isSelfCompany = companyId === user.activeCompany.id;
  const isNewCompany = companyId === 0;
  const isRiskTaker = user.roles.isRiskTaker;

  const getEmailsApi = useFetch(companyApi.getEmails, companyId);
  const addCompanyApi = useApi(companyApi.create);
  const updateCompanyApi = useApi(companyApi.update);
  const {
    data: commoditiesData,
    loading: commoditiesLoading,
    error: commoditiesError,
  } = useFetch(productsApi.getCommodities);

  const commodities: Commodity[] = (commoditiesData?.commodities || []).map((c: ICommodity) =>
    Commodity.create(c),
  );
  const isParentEditable =
    user.roles.isRiskTaker && !commodities.some((c) => c.selectable(companyId));

  const [isLoading, setIsLoading] = useState(false);
  const [isDemo, setIsDemo] = useState(company.isDemo);
  const [selectedView, setSelectedView] = useState(GENERAL_VIEW);
  const [viewPdf, setViewPdf] = useState(false);
  const [emails, setEmails] = useState<any[]>([]);
  const [emailTypes, setEmailTypes] = useState<ListItem[]>([]);
  const [emailChanges, setEmailChanges] = useState<any>({});

  const [files, setFiles, documents] = useAgreementDocuments(
    companyId > 0,
    companyApi.getAgreementDocuments,
    companyId,
  );

  const filterRoles = (parentRole: string | null) =>
    parentRole
      ? roles.filter((r) => parentChildRoles[parentRole].includes(r.value.toString()))
      : roles;

  const filterCompanies = (role: string): Company[] => {
    const parentRoles = role
      ? Object.keys(parentChildRoles).filter((r) => parentChildRoles[r].includes(role))
      : Object.keys(parentChildRoles);

    return companies.filter((c: Company) => parentRoles.includes(c.role));
  };

  const filterFeatures = (role: string): ListItem[] => {
    const rf = role ? roleFeatures[role] || {} : {};
    const featureIds = Object.keys(rf).map((k) => parseInt(k));

    return features
      .filter((f) => featureIds.includes(f.value as number))
      .map((f) => ({ ...f, disabled: isSelfCompany && !rf[f.value as number] }));
  };

  const [initialState, setInitialState] = useState<any>({});
  const [businessTypesList, setBusinessTypesList] = useState<ListItem[]>([]);
  const [featureList, setFeatureList] = useState<ListItem[]>([]);
  const [rolesList, setRolesList] = useState<ListItem[]>([]);
  const [parentCompanies, setParentCompanies] = useState<Company[]>([]);
  const [companyToEdit, setCompanyToEdit] = useState<any>(null);

  useEffect(() => {
    const data = { company, companies, roles };
    if (JSON.stringify(initialState) === JSON.stringify(data)) return;
    setInitialState(data);

    const { id, parentCompanyId, parentName } = company;
    const parentCompany = { ...Company.EMPTY, id: parentCompanyId, name: parentName };

    const initialRoles = filterRoles(company.parentSelection?.role || user.activeCompany.role);
    company.roleSelection =
      roles?.find((r) => r.value === company.role) ??
      (initialRoles.length === 1 ? initialRoles[0] : null);
    const role = company.role || company.roleSelection?.value;

    const initialCompanies = isSelfCompany ? [parentCompany] : filterCompanies(role);
    const initialFeatures = filterFeatures(role);

    company.parentSelection = isSelfCompany
      ? initialCompanies[0]
      : (companies?.find((c) => c.id === parentCompanyId) ??
        companies?.find((c) => c.id === user.activeCompany.id) ??
        (initialCompanies.length === 1 ? initialCompanies[0] : null));

    const parentBusinessTypes = company.parentSelection?.businessTypes ?? [];
    const initialBusinessTypes = businessTypes.filter(
      (t) => parentBusinessTypes.length === 0 || parentBusinessTypes.includes(t.value),
    );

    if (id === 0) {
      const parentFeatures = company.parentSelection?.features ?? [];
      company.features = initialFeatures
        .map((f) => f.value)
        .filter((f) => (parentFeatures ?? []).includes(f));
      company.businessTypes = parentBusinessTypes;
    }

    setBusinessTypesList(initialBusinessTypes);
    setFeatureList(initialFeatures);
    setParentCompanies(initialCompanies);
    setRolesList(initialRoles);

    setCompanyToEdit(company);
  }, [company, companies, roles]);

  const onRoleChange = (event: any, form: FormRenderProps) => {
    const role = event.value.value;
    const newCompanies = filterCompanies(role);
    const newFeatures = filterFeatures(role);

    const parent = form.valueGetter("parentSelection");
    if (parent && !newCompanies.find((c) => c.id === parent.id))
      form.onChange("parentSelection", { value: {} });

    form.onChange("features", {
      value: newFeatures.map((f) => f.value).filter((f) => (parent?.features ?? []).includes(f)),
    });

    setParentCompanies(newCompanies);
    setFeatureList(newFeatures);
  };

  const onParentChange = (event: any, form: FormRenderProps) => {
    const parent = event.value;
    const newFeatures = featureList
      .map((f) => f.value as number)
      .filter((f) => (parent?.features ?? []).includes(f));
    form.onChange("features", { value: newFeatures });

    const parentBusinessTypes = parent?.businessTypes ?? [];
    form.onChange("businessTypes", { value: parentBusinessTypes });

    setBusinessTypesList(businessTypes.filter((t) => parentBusinessTypes.includes(t.value)));
  };

  const onDemoChange = (event: any, form: FormRenderProps) => {
    const newValue = event.value;
    setIsDemo(newValue);
    if (newValue) form.onChange("dbcId", { value: null });
  };

  const onAdd = (event: UploadOnAddEvent, form: FormRenderProps) => {
    form.onChange("uploadFile", { value: JSON.stringify(event.newState) });

    setFiles(event.newState);
  };

  const onRemove = (event: UploadOnRemoveEvent, form: FormRenderProps) => {
    form.onChange("uploadFile", { value: null });
    setFiles(event.newState);
  };

  const onEditEmails = (isEditing: boolean, form: FormRenderProps) => {
    const value = isEditing ? undefined : "no";
    form.onChange("editing", { value: value });
  };

  useEffect(() => {
    setIsLoading(getEmailsApi.loading || commoditiesLoading);
  }, [getEmailsApi.loading, commoditiesLoading]);

  useEffect(() => {
    setEmailTypes(objectToLookup(getEmailsApi.data?.emailTypes ?? {}, false, false));
    if (getEmailsApi.data?.emails) setEmailChanges(getEmailsApi.data.emails);
  }, [getEmailsApi.data]);

  useEffect(() => {
    setEmails(emailChanges);
  }, [emailChanges]);

  useEffect(() => {
    if (!addCompanyApi.loading && (addCompanyApi.data || addCompanyApi.error))
      if (!addCompanyApi.error) props.onSubmit();
  }, [addCompanyApi.loading, addCompanyApi.data, addCompanyApi.error]);

  useEffect(() => {
    if (!updateCompanyApi.loading && (updateCompanyApi.data || updateCompanyApi.error))
      if (!updateCompanyApi.error) props.onSubmit();
  }, [updateCompanyApi.loading, updateCompanyApi.data, updateCompanyApi.error]);

  const [pdfData, setPdfData] = useState<{ bytes: any; fileName: string } | null>(null);
  const onOpenPdf = (document: any, fileName: string) => {
    // If document is not of type File, assume it is already the byte array
    setPdfData({ bytes: document, fileName: fileName });
    setViewPdf(true);
  };

  const CustomListItemUI = (props: UploadListItemProps) => {
    return (
      <>
        {props.files.map((file) => (
          <div style={{ display: "flex", justifyContent: "space-between", flexGrow: 1 }}>
            <div style={{ display: "flex" }}>
              <PdfFileItem dataItem={file} onOpenPdf={onOpenPdf} />
            </div>
            <Button
              onClick={() => props.onRemove(file.uid)}
              type="button"
              aria-label="Remove"
              title="Remove"
              icon={"x"}
            />
          </div>
        ))}
      </>
    );
  };

  const handleSubmitEdit = async (event: any) => {
    setIsLoading(true);

    const editCompany = { ...event };
    editCompany.parentCompanyId = event.parentSelection?.id;
    editCompany.role = event.roleSelection.value;
    delete editCompany.parentSelection;
    delete editCompany.roleSelection;

    const uploadedFiles =
      files?.length > 0
        ? files.filter((f) => isNaN(Number(f.uid))).map((f) => f.getRawFile!())
        : null;

    if (editCompany.id === 0) {
      await addCompanyApi.request(editCompany, uploadedFiles, emailChanges);
    } else {
      const removedDocumentIds = documents
        .filter((doc) => !files.some((file) => file.uid === doc.agreementDocumentId.toString()))
        .map((doc) => doc.agreementDocumentId);

      await updateCompanyApi.request(editCompany, uploadedFiles, removedDocumentIds, emailChanges);
    }

    setIsLoading(false);
  };

  const switchTabs = (value: string, isEditing: string) => {
    if (selectedView !== CONTACTS_VIEW || (selectedView === CONTACTS_VIEW && isEditing?.length > 0))
      setSelectedView(value);
  };

  const contactsValidator = (value: string) => {
    const message = requiredValidator(value);
    return message.length > 0 ? "Please complete editing before leaving the tab" : "";
  };

  return (
    <>
      {companyToEdit && (
        <Dialog
          title={isNewCompany ? "Add New Company" : `Edit ${company.name}`}
          onClose={props.cancelEdit}
        >
          <div style={{ minWidth: "400px" }}>
            <Form
              onSubmit={handleSubmitEdit}
              initialValues={companyToEdit}
              render={(formRenderProps: FormRenderProps) => (
                <FormElement>
                  <HeaderTextSelector
                    values={[GENERAL_VIEW, DOCUMENTS_VIEW, CONTACTS_VIEW]}
                    onChange={(tab) => switchTabs(tab, formRenderProps.valueGetter("editing"))}
                    selected={selectedView}
                  />

                  {selectedView === GENERAL_VIEW && (
                    <>
                      <Field
                        label="Role"
                        name="roleSelection"
                        component={FormDropDownList}
                        validator={requiredValidator}
                        data={rolesList}
                        disabled={!isNewCompany}
                        textField="label"
                        dataItemKey="value"
                        onChange={(e) => onRoleChange(e, formRenderProps)}
                      />
                      <Field
                        label="Parent Company"
                        name="parentSelection"
                        component={FormDropDownList}
                        validator={parentCompanies.length > 0 ? requiredValidator : undefined}
                        data={parentCompanies}
                        disabled={
                          (parentCompanies.length === 0 || !isNewCompany) && !isParentEditable
                        }
                        textField="name"
                        dataItemKey="id"
                        onChange={(e) => onParentChange(e, formRenderProps)}
                      />
                      <Field
                        label="Company Name"
                        name="name"
                        component={FormInput}
                        validator={requiredValidator}
                        autoComplete="off"
                      />

                      {isRiskTaker && !isDemo && (
                        <Field
                          label="DBC Id"
                          name="dbcId"
                          component={FormInput}
                          maxLength={10}
                          validator={[alphaNumericValidator, requiredValidator]}
                          autoComplete="off"
                        />
                      )}

                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <div style={{ display: "block" }}>
                          {featureList.length > 0 && (isSelfCompany || isRiskTaker) && (
                            <Field
                              label="Features"
                              name="features"
                              id="features"
                              component={FormSwitchList}
                              data={featureList}
                              disabled={isLoading}
                            />
                          )}

                          {!isSelfCompany && (
                            <>
                              <Field
                                label="Active"
                                name="isActive"
                                component={FormSwitch}
                                defaultChecked={company.isActive}
                                disabled={isLoading}
                              />

                              <Field
                                label="Demo"
                                name="isDemo"
                                component={FormSwitch}
                                defaultChecked={company.isDemo}
                                disabled={isLoading}
                                onChange={(e) => onDemoChange(e, formRenderProps)}
                              />
                            </>
                          )}
                        </div>
                        <div>
                          {(isRiskTaker || businessTypesList.length > 1) && (
                            <Field
                              label="Line of Business"
                              name="businessTypes"
                              id="businessTypes"
                              component={FormSwitchList}
                              data={businessTypesList}
                              disabled={isLoading || isSelfCompany}
                            />
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {selectedView === DOCUMENTS_VIEW && (
                    <>
                      <Label style={{ paddingTop: "20px" }}>Agreement Documents</Label>
                      <Label style={{ fontStyle: "italic" }}>
                        Any documents listed will need to be agreed upon by users of this company.
                      </Label>
                      <Field
                        name="uploadFile"
                        component={FormInput}
                        style={{ visibility: "hidden", display: "none" }}
                      />
                      {!isLoading && (
                        <Upload
                          id="uploadPdf"
                          batch={false}
                          files={files}
                          multiple={true}
                          onAdd={(e) => onAdd(e, formRenderProps)}
                          onRemove={(e) => onRemove(e, formRenderProps)}
                          withCredentials={false}
                          autoUpload={false}
                          listItemUI={CustomListItemUI}
                          restrictions={{
                            allowedExtensions: [".pdf"],
                          }}
                        />
                      )}
                    </>
                  )}

                  {selectedView === CONTACTS_VIEW && (
                    <>
                      <Label style={{ paddingTop: "20px" }}>
                        Email distribution list to which notifications about quotes will be sent:
                      </Label>

                      <EmailListEditor
                        emails={emails}
                        emailTypes={emailTypes}
                        onChange={setEmailChanges}
                        onEdit={(isEditing: boolean) => onEditEmails(isEditing, formRenderProps)}
                      />

                      <Field
                        name="editing"
                        component={FormInput}
                        validator={contactsValidator}
                        style={{ visibility: "hidden", display: "none" }}
                      />
                    </>
                  )}

                  <div>
                    {commoditiesError && <Error>{commoditiesError}</Error>}
                    {getEmailsApi.error && <Error>{getEmailsApi.error}</Error>}
                    {updateCompanyApi.error && <Error>{updateCompanyApi.error}</Error>}
                    {addCompanyApi.error && <Error>{addCompanyApi.error}</Error>}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      paddingTop: "1rem",
                    }}
                  >
                    <LoadingIndicator loading={isLoading} />

                    {!isLoading && (
                      <>
                        <Button themeColor="base" disabled={isLoading} onClick={props.cancelEdit}>
                          Cancel
                        </Button>

                        <Button
                          themeColor="primary"
                          type="submit"
                          disabled={!formRenderProps.allowSubmit}
                        >
                          Save
                        </Button>
                      </>
                    )}
                  </div>
                </FormElement>
              )}
            ></Form>
          </div>
        </Dialog>
      )}

      {viewPdf && pdfData && (
        <ViewPdfDocumentDialog
          document={pdfData.bytes}
          onClose={() => setViewPdf(false)}
          fileName={pdfData.fileName}
        />
      )}
    </>
  );
};
